// MediaAdditionsChat.tsx
import * as React from "react";
import type { NavigateFunction, Location } from "react-router-dom";
import type { Message } from "../../../shared/chat/chatTypes";
import { idOf } from "../../../shared/chat/idOf";
import { applyAlbumManifestInPlace } from "../../../shared/chat/chatExpiry";
import { parseAlbumIds } from "../../../shared/chat/chatMessageUtils";

type ShowMediaArgs = {
  msg: Message;
  selectedUserId?: string | null;
  myUser: any; // keep flexible; Chat already uses "user" from useAuth
  navigate: NavigateFunction;
  lastSentIdsRef: React.MutableRefObject<string[] | null>;
};

export function showMediaForMessageImpl({
  msg,
  selectedUserId,
  myUser,
  navigate,
  lastSentIdsRef,
}: ShowMediaArgs) {
  const partnerId = selectedUserId;
  if (!partnerId) return;

  // ensure album manifest is applied if decryptedText still contains it
  applyAlbumManifestInPlace(msg as any);

  const albumItems = (msg as any).albumItems;
  const albumIds = (msg as any).albumIds ?? parseAlbumIds(msg);

  // CASE 1: mobile-style album (inline R2 items, no child message ids)
  if (Array.isArray(albumItems) && albumItems.length) {
    const me = idOf(myUser);
    const isSender =
      idOf((msg as any).sender) === me ||
      idOf((msg as any).senderId) === me;
  
    const parentIdStr = String((msg as any)._id);
  
    navigate(`/chat/${partnerId}/media`, {
      state: {
        albumItems,
        parentId: parentIdStr,
        isSender,
  
        // ✅ IMPORTANT: tell presentation exactly what message to "close"
        closeMessageId: parentIdStr,
      },
    });
    return;
  }

  // CASE 2: legacy/by-IDs album (web-style)
  let onlyIds: string[] = [];
  let parentId: string | undefined;

  if (albumIds && albumIds.length) {
    onlyIds = albumIds;
    parentId = String((msg as any)._id);
  } else {
    const clickedId = String((msg as any)._id);
    const batch = lastSentIdsRef.current?.map(String) || [];
    onlyIds = batch.includes(clickedId) ? batch.slice() : [clickedId];
  }

  lastSentIdsRef.current = null;

  const closeMessageId = parentId ? parentId : String(onlyIds[0]);

  navigate(`/chat/${partnerId}/media`, {
    state: parentId
      ? { onlyIds, parentId, closeMessageId } // ✅ add closeMessageId
      : { onlyIds, closeMessageId }, 
    });
}

type UseMediaAdditionsArgs = {
  selectedUserId?: string | null;
  myUser: any;
  navigate: NavigateFunction;
  location: Location;
  // used to scrub state when `justSentIds` exists
  scrubState?: {
    toUserId?: string;
    toUserName?: string;
    toUserPicture?: string;
  };
};

export function useMediaAdditionsChat({
  selectedUserId,
  myUser,
  navigate,
  location,
  scrubState,
}: UseMediaAdditionsArgs) {
  // snapshot of message IDs returned from media upload route
  const lastSentIdsRef = React.useRef<string[] | null>(null);

  // expose handler used by MessageOutlook
  const showMediaForMessage = React.useCallback(
    (msg: Message) =>
      showMediaForMessageImpl({
        msg,
        selectedUserId,
        myUser,
        navigate,
        lastSentIdsRef,
      }),
    [selectedUserId, myUser, navigate]
  );

  // A) if navigation state includes `justSentIds`, store + scrub it (so it doesn't persist)
  React.useEffect(() => {
    const st = (location.state || {}) as any;
    if (Array.isArray(st.justSentIds) && st.justSentIds.length) {
      lastSentIdsRef.current = st.justSentIds.map(String);

      // scrub transient state so back/forward history stays clean
      navigate(location.pathname, {
        replace: true,
        state: { ...st, justSentIds: undefined },
      });
    }
  }, [location.pathname, location.state, navigate]);

  // B) (optional) your other `justSentIds` variant (the one that rebuilds state)
  // If you want to keep EXACT behavior, keep this too (it matches your current code path).
  React.useEffect(() => {
    const st = (location.state || {}) as any;
    const js: string[] | undefined = st.justSentIds;

    if (Array.isArray(js) && js.length) {
      lastSentIdsRef.current = js.slice();

      // optional: clear the state so it doesn't persist on back/forward
      navigate(location.pathname, {
        replace: true,
        state: scrubState ? { ...scrubState } : { ...st, justSentIds: undefined },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/* keep stable on purpose */]);

  return { lastSentIdsRef, showMediaForMessage };
}

type MediaButtonArgs = {
  disabled?: boolean;
  title: string;
  style: React.CSSProperties;
  onClick: () => void;
  children?: React.ReactNode;
};

export function MediaButton({ disabled, title, style, onClick, children }: MediaButtonArgs) {
  return (
    <button type="button" title={title} style={style} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}
